import { Calendar, FlaskConical, Star, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../config/api'

interface CreatorStats {
  owner_id: number
  display_name: string
  avatar_url: string | null
  level: number
  total_xp: number
  member_since: string
  project_count: number
  completed_count: number
  follower_count: number
  total_received_flz: number
}

interface ProjectCreatorProps { projectId: number; isResearch?: boolean }

export default function ProjectCreator({ projectId, isResearch }: ProjectCreatorProps) {
  const [stats, setStats] = useState<CreatorStats | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(isResearch ? `${API_URL}/api/research/projects/${projectId}/creator-stats` : `${API_URL}/api/projects/${projectId}/creator-stats`)
        if (res.ok) {setStats(await res.json() as CreatorStats)}
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [projectId])

  if (loading) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}
  if (!stats) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Creator info unavailable</div>}

  // Null safety defaults
  const dn = stats.display_name || 'Unknown'
  const lv = stats.level || 1
  const xp = stats.total_xp || 0
  const ms = stats.member_since || new Date().toISOString()

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Profile card */}
      <div
        onClick={() => stats.owner_id && navigate(`/profile/${stats.owner_id}`)}
        style={{
        background: 'var(--card-bg)', border: '1px solid var(--border-color)',
        borderRadius: 14, padding: '24px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20,
        cursor: 'pointer'
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
      >
        <div style={{
          width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 28, fontWeight: 700
        }}>
          {(stats.display_name || 'U').charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
            {dn}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
            <Star size={13} style={{ color: '#f59e0b' }} />
            Level {lv} · {xp.toLocaleString()} XP
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
            <Calendar size={12} />
            Member since {new Date(ms).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {[
          { icon: <FlaskConical size={16} />, value: stats.project_count ?? 0, label: 'Projects', color: '#6366f1' },
          { icon: <FlaskConical size={16} />, value: stats.completed_count ?? 0, label: 'Completed', color: '#10b981' },
          { icon: <Users size={16} />, value: (stats.follower_count ?? 0).toLocaleString(), label: 'Followers', color: '#3b82f6' },
          { icon: <Star size={16} />, value: `${(stats.total_received_flz ?? 0).toLocaleString()} FLZ`, label: 'Received', color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: 12, padding: '16px 18px',
            display: 'flex', alignItems: 'center', gap: 12
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: s.color + '18', color: s.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
